import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserAuthService } from '../UserAuth.service';
import {
  CompanyPreferences,
  GlobalPreferences,
  PreferenceTier,
  SavedProjectGridView,
  DEFAULT_COMPANY_PREFERENCES,
  DEFAULT_EFFECTIVE_PREFERENCES,
  EffectivePreferences,
  PreferencesApiResponse,
  UserPreferences,
} from './preferences.types';

@Injectable({
  providedIn: 'root',
})
export class PreferencesService {
  effective$ = new BehaviorSubject<EffectivePreferences>(DEFAULT_EFFECTIVE_PREFERENCES);
  company$ = new BehaviorSubject<CompanyPreferences>(DEFAULT_COMPANY_PREFERENCES);
  /** The global tier, for an admin UI that shows which value came from where. */
  global$ = new BehaviorSubject<GlobalPreferences | null>(null);
  user$ = new BehaviorSubject<UserPreferences>({
    language: null,
    measurementSystem: null,
    dateFormat: null,
    timeFormat: null,
    theme: null,
    rowsPerPage: null,
    temp3DRange: null,
    timeIn3DGraph: null,
    chartsColorPalette: null,
    chartsAutoRefreshIntervalS: null,
    projectGridView: null,
    projectListColumnOrder: null,
    projectListDefaultStatusFilter: null,
    projectListDefaultDateRange: null,
    wizardTemplateId: null,
    exportsDefaultTemplate: null,
    exportsDefaultPdfSections: null,
    fiberTimer: null,
    notificationsEmailEnabled: null,
    notificationsRealtimeAlerts: null,
    notifyOnPreferenceOverwrite: null,
    notificationsNtfyTopic: null,
    notificationsNtfyEnabled: null,
    cureDefaultSheetId: null,
  });
  loadError$ = new BehaviorSubject<string | null>(null);

  /**
   * TRUE once the user's measurement preference is known — from cache or from the
   * server — rather than merely defaulted.
   *
   * effective$ ALWAYS holds a usable value, which is exactly why it cannot answer
   * "do we know yet?": its opening value is DEFAULT_EFFECTIVE_PREFERENCES, whose
   * measurementSystem is 'metric'. A subscriber cannot tell a real metric
   * preference from the placeholder, so anything that must not guess — the
   * unitCode on a data request — needs this separate signal.
   *
   * Stays false when there is genuinely nothing to know (no cache, load skipped
   * for want of an identity). Callers must bound their wait rather than block
   * forever; see ProjectService.afterUnitsSettle.
   */
  settled$ = new BehaviorSubject<boolean>(false);

  private loaded = false;
  /**
   * The customerId the last completed load was scoped to, or null if it went out
   * UNSCOPED. Preferences are keyed per customer, so an unscoped answer is a
   * different question's answer, not this customer's.
   */
  private loadedForCustomerId: string | null = null;

  constructor(
    private http: HttpClient,
    private userAuth: UserAuthService,
  ) {
    this.hydrateFromCache();

  }

  /** Set once the customer watch is armed; see watchCustomer(). */
  private customerWatch?: { unsubscribe(): void };

  /**
   * ARMED FROM load(), NOT THE CONSTRUCTOR.
   *
   * Subscribing in the constructor made every consumer's test responsible for
   * providing activeCustomer$ — a dozen spec doubles that have nothing to do with
   * preferences started failing on "cannot read properties of undefined". load() is
   * the real entry point and is always called in the app, so arming here costs
   * nothing and burdens nobody.
   */
  private watchCustomer(): void {
    if (this.customerWatch) {
      return;
    }

    // CLAIM THE SLOT BEFORE SUBSCRIBING. This line is the fix for a 203-request
    // burst on every project open.
    //
    // activeCustomer$ is a BehaviorSubject, so subscribe() delivers the current
    // value SYNCHRONOUSLY, inside the call below — and that callback calls
    // load(), which calls this method again. The assignment to `customerWatch`
    // was waiting for subscribe() to RETURN, so on re-entry the guard above saw
    // undefined and subscribed again:
    //
    //     load -> watchCustomer -> subscribe -> cb -> load -> watchCustomer -> ...
    //
    // Each level issued a GET and leaked a subscription; the recursion ended only
    // when the JS stack did. Measured on beta-dev: 203 identical requests inside
    // 29 ms, and in the spec harness 1281 requests with 669 live subscriptions.
    //
    // A placeholder closes the window between "we have decided to subscribe" and
    // "we have a subscription to record", which is the window the re-entry lived
    // in. It is replaced by the real subscription immediately below.
    this.customerWatch = { unsubscribe: () => undefined };
  
      // RE-LOAD WHEN THE CUSTOMER FINALLY RESOLVES.
      //
      // load() is called ONCE, on the first authenticated emission (main-layout's
      // isAuthenticated$ ... take(1)). If the active customer has not been restored by
      // then — a first sign-in on a machine, or any load with the customer cleared —
      // the GET goes out with NO customerId, the server answers customer-agnostically,
      // and that answer used to latch settled$ as final. Preferences are keyed per
      // customer, so it is the wrong record, and nothing ever asked again.
      //
      // Measured on prod2 by the unit-preference session: stored preference IMPERIAL,
      // cold client renders unitCode=0, axes 0-100.00m / 0-180C. Warm client renders
      // unitCode=1, 0-300.00ft / 50-350F. Both internally consistent — the units
      // mismatch really is fixed — but the cold one applies the wrong preference
      // entirely. A first sign-in on a new laptop shows metric, which is exactly what
      // Rob does on the morning of a demo.
      //
      // NOT the 4s settle timeout, which was the first hypothesis: the prod2
      // preferences endpoint answers in ~0.17 s, far inside the 4000 ms bound.
      const watch = this.userAuth.activeCustomer$.subscribe((customer) => {
        const id = customer?.CustomerId ?? '';
        if (!id) {
          return;
        }
        if (this.loadedForCustomerId === id) {
          return;
        }
        // Either we have never loaded, or the last load was unscoped or for a
        // different customer. Ask the right question.
        this.load();
      });
      this.customerWatch = watch;
  }

  /**
   * The request currently in flight, as its parameter triple.
   *
   * SECOND HALF OF THE BURST FIX. Closing the re-entrancy window above stops the
   * unbounded recursion, but the synchronous replay still re-enters load() ONCE —
   * and an identical GET issued while the identical GET is still in flight is
   * waste whatever caused it. `loadedForCustomerId` cannot cover this: it is
   * assigned in the RESPONSE handler, so inside a synchronous burst it is null at
   * every level.
   *
   * Keyed by the parameters rather than a boolean, so a genuine change — the
   * unscoped first load followed by the scoped one when the customer resolves —
   * is a DIFFERENT question and still gets asked. That re-ask is the whole reason
   * the customer watch exists; suppressing it would trade this defect for the
   * cold-load wrong-preference bug it was built to fix.
   */
  private inFlightKey: string | null = null;

  load(): void {
    this.watchCustomer();
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();

    if (!tenantId || !objectId) {
      return;
    }

    // HYDRATE AGAIN, NOW THAT THERE IS AN IDENTITY TO KEY ON.
    //
    // The constructor also calls this, and on a full page reload it always
    // early-returns: the cache key is scoped by tenant and object id, and MSAL
    // has not settled when services are constructed. So the cached preference
    // was only ever applied on a soft navigation — which is precisely why the
    // same URL rendered imperial after an in-app nav and metric after F5.
    //
    // load() is called once, on the first authenticated emission, so the
    // identity exists by the time we arrive here. This makes the cached
    // preference available SYNCHRONOUSLY, before the network round-trip, so a
    // returning user's first request already carries the right unitCode and the
    // gate below costs them nothing.
    this.hydrateFromCache();

    const key = `${tenantId}|${objectId}|${customerId}`;
    if (this.inFlightKey === key) {
      return;
    }
    this.inFlightKey = key;

    let params = new HttpParams()
      .set('tenantId', tenantId)
      .set('objectId', objectId);
    if (customerId) {
      params = params.set('customerId', customerId);
    }

    this.http
      .get<PreferencesApiResponse>(`${environment.apiUrl}v1/preferences`, { params })
      .pipe(
        catchError((err) => {
          console.warn('PreferencesService > load failed, using cached values', err);
          this.loadError$.next('Failed to load preferences. Using cached values.');
          return of(null);
        })
      )
      .subscribe((response) => {
        // The question has been answered; the next caller may ask it again.
        this.inFlightKey = null;
        // SETTLE ON EITHER OUTCOME. catchError maps a failure to null, and a
        // failed load is still the end of learning: we know as much as we are
        // ever going to know on this attempt, and holding the gate open past
        // that would stall every data request behind an error.
        // SETTLE ONLY ON AN ANSWER WE CAN TRUST.
        //
        // A response to an UNSCOPED request is not this customer's preference, and
        // latching it closes the gate on a guess. Leaving settled$ false keeps the
        // data request waiting for the scoped re-load that the activeCustomer$
        // subscription above will trigger — and if the customer never resolves, the
        // caller's existing 4s bound still releases everything, so this can delay a
        // cold load but cannot hang it.
        //
        // An ERROR still settles: catchError maps failure to null, and a failed load
        // is the end of learning on this attempt whatever its scope.
        if (customerId || !response) {
          this.settled$.next(true);
        }
        if (!response) return;
        this.loadedForCustomerId = customerId || null;
        this.effective$.next(response.effective);
        this.company$.next(response.company);
        this.user$.next(response.user);
        this.global$.next(response.global ?? null);
        this.loadError$.next(null);
        this.writeCache(response.effective, tenantId, objectId);
        this.loaded = true;
      });
  }

  saveUser(patch: Partial<UserPreferences>): Observable<EffectivePreferences> {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();
    const fieldUpdatedAt: Record<string, string> = {};
    const now = new Date().toISOString();
    Object.keys(patch).forEach((k) => (fieldUpdatedAt[k] = now));

    // Optimistic update: apply non-null patch values onto the current effective state.
    const optimisticEffective: EffectivePreferences = { ...this.effective$.value };
    (Object.keys(patch) as Array<keyof UserPreferences>).forEach((key) => {
      const val = patch[key];
      if (val != null) (optimisticEffective as any)[key] = val;
    });
    this.effective$.next(optimisticEffective);
    this.writeCache(optimisticEffective, tenantId, objectId);

    const params = new HttpParams();

    return this.http
      .put<PreferencesApiResponse>(
        `${environment.apiUrl}v1/preferences/user`,
        { tenantId, customerId, objectId, patch, fieldUpdatedAt },
        { params }
      )
      .pipe(
        map((response) => {
          this.effective$.next(response.effective);
          this.user$.next(response.user);
          this.writeCache(response.effective, tenantId, objectId);
          return response.effective;
        }),
        catchError((err) => {
          console.error('PreferencesService > saveUser failed', err);
          throw err;
        })
      );
  }

  saveCompany(patch: Partial<CompanyPreferences>): Observable<EffectivePreferences> {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();
    const fieldUpdatedAt: Record<string, string> = {};
    const now = new Date().toISOString();
    Object.keys(patch).forEach((k) => (fieldUpdatedAt[k] = now));

    const params = new HttpParams();

    return this.http
      .put<PreferencesApiResponse>(
        `${environment.apiUrl}v1/preferences/company`,
        { tenantId, customerId, objectId, patch, fieldUpdatedAt },
        { params }
      )
      .pipe(
        map((response) => {
          this.effective$.next(response.effective);
          this.company$.next(response.company);
          this.writeCache(response.effective, tenantId, objectId);
          return response.effective;
        }),
        catchError((err) => {
          console.error('PreferencesService > saveCompany failed', err);
          throw err;
        })
      );
  }

  /**
   * Save the grid view to ONE tier.
   *
   * THREE-WAY, and the distinction is the contract the server enforces:
   *   an object      -> replace this tier's view wholesale
   *   explicit null  -> CLEAR this tier, so the cascade falls through to the one
   *                     below. This is the "Reset to <tier> default" button, and an
   *                     explicit null is the only way to express it.
   *   omitting the key entirely (i.e. never calling this) -> unchanged.
   *
   * Replace rather than deep-merge, because a merge cannot express "I removed a
   * column" or "I cleared the grouping" — both are absences in the payload.
   */
  saveProjectGridView(
    tier: PreferenceTier,
    view: SavedProjectGridView | null
  ): Observable<EffectivePreferences> {
    if (tier === 'global') return this.saveGlobal(view);
    if (tier === 'company') return this.saveCompany({ projectGridView: view });
    return this.saveUser({ projectGridView: view });
  }

  /**
   * Write the product-wide defaults. SuperAdmin only, enforced server-side from the
   * TOKEN — the client hiding the button is an affordance, not the control.
   *
   * Sends no tenantId and no customerId: a global write is scoped to neither, and
   * the row it touches is not the tenant-wide company document.
   */
  saveGlobal(view: SavedProjectGridView | null): Observable<EffectivePreferences> {
    const now = new Date().toISOString();
    return this.http
      .put<{ global: GlobalPreferences }>(`${environment.apiUrl}v1/preferences/global`, {
        patch: { projectGridView: view },
        fieldUpdatedAt: { projectGridView: now },
      })
      .pipe(
        map((response) => {
          this.global$.next(response.global ?? null);
          // The global tier only shows through where nothing more specific overrides
          // it, so the effective view is re-read rather than guessed at here.
          this.refresh();
          return this.effective$.value;
        }),
        catchError((err) => {
          console.error('PreferencesService > saveGlobal failed', err);
          throw err;
        })
      );
  }

  uploadLogo(
    file: File,
    opts?: { removeBackground?: boolean; bgTolerance?: number; cropRect?: string }
  ): Observable<string> {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();
    const formData = new FormData();
    formData.append('logo', file);
    if (opts?.removeBackground) formData.append('removeBackground', 'true');
    if (opts?.bgTolerance != null) formData.append('bgTolerance', String(opts.bgTolerance));
    if (opts?.cropRect) formData.append('cropRect', opts.cropRect);

    let params = new HttpParams()
      .set('tenantId', tenantId)
      .set('objectId', objectId);
    if (customerId) {
      params = params.set('customerId', customerId);
    }

    return this.http
      .post<{ reportLogoUrl?: string; brandLogoUrl?: string }>(
        `${environment.apiUrl}v1/preferences/company/logo`,
        formData,
        { params }
      )
      .pipe(
        map((response) => {
          const url = response.reportLogoUrl ?? response.brandLogoUrl ?? '';
          // Reflect the new logo where it's displayed, but do NOT push a full
          // company object here: that would re-run populateCompanyForm and wipe
          // any unsaved brand-color / size edits (e.g. colors just auto-detected
          // from this very logo). Brand colors are persisted via saveCompany.
          this.effective$.next({ ...this.effective$.value, reportLogoUrl: url, brandLogoUrl: url });
          return url;
        }),
        catchError((err) => {
          console.error('PreferencesService > uploadLogo failed', err);
          throw err;
        })
      );
  }

  refresh(): void {
    this.loaded = false;
    this.load();
  }

  private hydrateFromCache(): void {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    if (!tenantId || !objectId) return;

    const cached = localStorage.getItem(this.cacheKey(tenantId, objectId));
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as EffectivePreferences;
        this.effective$.next(parsed);
        // A cached preference is a KNOWN preference. Waiting for the network to
        // confirm what we already have on disk would reintroduce the delay this
        // whole mechanism exists to remove.
        this.settled$.next(true);
      } catch {
        // ignore malformed cache
      }
    }
  }

  private writeCache(effective: EffectivePreferences, tenantId: string, objectId: string): void {
    try {
      localStorage.setItem(this.cacheKey(tenantId, objectId), JSON.stringify(effective));
    } catch {
      // ignore storage errors (e.g. private browsing quota)
    }
  }

  // Cache key is scoped by customer so two customers sharing a tenant don't share cache.
  private cacheKey(tenantId: string, objectId: string): string {
    const customerId = this.userAuth.getActiveCustomerId();
    return customerId
      ? `prefs:${tenantId}:${customerId}:${objectId}`
      : `prefs:${tenantId}:${objectId}`;
  }

  private getObjectId(): string {
    return this.userAuth.getObjectId();
  }
}
